import { Core } from "@faire/design-tokens";
import { getRetailerOrBrandToken } from "@faire/help-center-utils/serialized-data/getRetailerOrBrandToken";
import { Button } from "@faire/web--source/slate/Button";
import { Column } from "@faire/web--source/slate/Layout";
import { Spacer } from "@faire/web--source/slate/spacing";
import { Typography } from "@faire/web--source/slate/Typography";
import { useViewport } from "@faire/web--source/ui/hooks/useViewport";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { trackCxFoundationsTellUsMorePageNextButtonClick } from "@faire/web-api--source/events/cxFoundations/click/tellUsMorePageNextButton";
import { trackCxFoundationsTellUsMoreView } from "@faire/web-api--source/events/cxFoundations/view/tellUsMore";
import React, { useCallback, useEffect, useRef } from "react";
import styled from "styled-components";

import { BackButton } from "../../components/BackButton";
import { IssueTopics } from "../../components/IssueTopics";
import { useNavigationStore } from "../../hooks/NavigationContext";
import { useSelfServeContextStore } from "../../hooks/SelfServeContext";
import { useCurrentNodeHasMoreLevels } from "../../hooks/useCurrentNodeHasMoreLevels";
import { useIssueType } from "../../hooks/useIssueType";
import { useLevelNodesSectionTitles } from "../../hooks/useLevelNodesSectionTitles";
import { useNavigation } from "../../hooks/useNavigation";
import { useNodeSetter } from "../../hooks/useNodeSetter";
import { useSelectedNode } from "../../hooks/useSelectedNode";
import { useTrackPageViewEffect } from "../../hooks/useTrackPageViewEffect";

import { LevelNodesSection } from "./LevelNodesSection";

export const MoreIssue: React.FC = () => {
  useTrackPageViewEffect(trackCxFoundationsTellUsMoreView);

  const { level1Node, level2Node, level3Node, level4Node, level5Node } =
    useSelectedNode();
  const { setLevel2Node, setLevel3Node, setLevel4Node, setLevel5Node } =
    useNodeSetter();
  const [{ brandOrders, selectedBrandOrder }] = useSelfServeContextStore([
    "brandOrders",
    "selectedBrandOrder",
  ]);
  const { goToNextStep } = useNavigation();
  const { issueType } = useIssueType();
  const sectionTitles = useLevelNodesSectionTitles();
  const goToNextStepRef = useRef(goToNextStep);
  const bottomElementRef = useRef<HTMLDivElement | null>(null);
  const { isMobile } = useViewport();
  const [{ hasLevelQueryParams }, setNavigationStore] = useNavigationStore([
    "hasLevelQueryParams",
  ]);

  const shouldShowLevel3Nodes =
    useCurrentNodeHasMoreLevels(2).hasMoreIssueLevels;
  const shouldShowLevel4Nodes =
    useCurrentNodeHasMoreLevels(3).hasMoreIssueLevels;
  const shouldShowLevel5Nodes =
    useCurrentNodeHasMoreLevels(4).hasMoreIssueLevels;

  const shouldShowNextButton = useCallback(() => {
    const hasNotSelectedLevel3Node = shouldShowLevel3Nodes && !level3Node;
    const hasNotSelectedLevel4Node = shouldShowLevel4Nodes && !level4Node;
    const hasNotSelectedLevel5Node = shouldShowLevel5Nodes && !level5Node;

    return !(
      !level2Node ||
      hasNotSelectedLevel3Node ||
      hasNotSelectedLevel4Node ||
      hasNotSelectedLevel5Node
    );
  }, [
    level2Node,
    level3Node,
    level4Node,
    level5Node,
    shouldShowLevel3Nodes,
    shouldShowLevel4Nodes,
    shouldShowLevel5Nodes,
  ]);

  const handleTellUsMoreNextButtonClick = () => {
    goToNextStep();
    trackCxFoundationsTellUsMorePageNextButtonClick(
      issueType,
      getRetailerOrBrandToken()
    );
  };

  // If the user has selected a level query param, go to the next step
  useEffect(() => {
    if (shouldShowNextButton() && hasLevelQueryParams) {
      goToNextStepRef.current();
      setNavigationStore({ hasLevelQueryParams: false });
    }
  }, [hasLevelQueryParams, setNavigationStore, shouldShowNextButton]);

  const hasOrderSelected =
    brandOrders && brandOrders.length > 0 && selectedBrandOrder !== undefined;

  return (
    <Wrapper data-test-id="tell-us-more-wrapper">
      <BackButton />
      {isMobile ? null : <Spacer height="3x" />}
      <Typography variant="sectionHeaderSansMedium">
        <StrictLocalMsg
          defaultMessage="Tell us more"
          description={{
            text: "Title for asking more information from user",
          }}
        />
      </Typography>
      <LevelNodesSection
        title={
          hasOrderSelected
            ? sectionTitles.whatIssue
            : sectionTitles.whatIssueWithoutOrder
        }
      >
        <IssueTopics
          parentNode={level1Node}
          selectedNode={level2Node}
          setSelectedNode={setLevel2Node}
          isAutofocusOnMobile={true}
          bottomElementRef={bottomElementRef}
        />
      </LevelNodesSection>

      <LevelNodesSection
        visibleWhen={shouldShowLevel3Nodes}
        title={sectionTitles.chooseCategory}
      >
        <IssueTopics
          parentNode={level2Node}
          selectedNode={level3Node}
          setSelectedNode={setLevel3Node}
          isAutofocusOnMobile={true}
          bottomElementRef={bottomElementRef}
        />
      </LevelNodesSection>

      <LevelNodesSection
        visibleWhen={shouldShowLevel4Nodes}
        title={sectionTitles.chooseType}
      >
        <IssueTopics
          parentNode={level3Node}
          selectedNode={level4Node}
          setSelectedNode={setLevel4Node}
          isAutofocusOnMobile={true}
          bottomElementRef={bottomElementRef}
        />
      </LevelNodesSection>

      <LevelNodesSection
        visibleWhen={shouldShowLevel5Nodes}
        title={sectionTitles.chooseSubType}
      >
        <IssueTopics
          parentNode={level4Node}
          selectedNode={level5Node}
          setSelectedNode={setLevel5Node}
          isAutofocusOnMobile={true}
          bottomElementRef={bottomElementRef}
        />
      </LevelNodesSection>

      {shouldShowNextButton() ? (
        <>
          <Spacer height="2x" />
          <Button
            onClick={handleTellUsMoreNextButtonClick}
            data-test-id="tell-us-more-next-btn"
          >
            <StrictLocalMsg
              defaultMessage="Next"
              description={{
                text: "CTA button to go to next page",
              }}
            />
          </Button>
        </>
      ) : null}
      <BottomAnchor ref={bottomElementRef} />
    </Wrapper>
  );
};

const Wrapper = styled(Column).attrs({ align: "flex-start" })`
  background-color: ${Core.surface.inverse};
  min-height: 100%;
`;

const BottomAnchor = styled.div`
  height: 100px;
`;
